function _define_property(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
import * as React from 'react';
import { useContext, useMemo, useRef } from 'react';
import { useIsomorphicLayoutEffect } from '../util';
import { toAnimationTask, emptyAnimationTask } from './animate';
function noopTimeline() {
    return {
        register: (_type, _anim)=>{
        // Do nothing
        },
        start: (_type)=>emptyAnimationTask(),
        prepare: (_type)=>undefined,
        has: (_type)=>false,
        listen: (handlers)=>{
            handlers.in && handlers.in();
            return ()=>handlers.out && handlers.out();
        },
        // eslint-disable-next-line @typescript-eslint/no-empty-function
        fire (_) {}
    };
}
class AnimationTimeline {
    fire(type) {
        this.eventHandlers[type].forEach((handler)=>handler());
    }
    register(type, anim) {
        let anims = this.animations.get(type);
        if (anims == null) {
            anims = [];
            this.animations.set(type, anims);
        }
        anims.push(anim);
        return ()=>{
            if (anims) {
                const idx = anims.indexOf(anim);
                if (idx !== -1) {
                    anims.splice(idx, 1);
                }
            }
        };
    }
    start(type) {
        this.prepare(type);
        const anims = this.animations.get(type) || [];
        // Note that only "push" `out` animations. `in` animations are special and
        // we always consider them one-shot, but `out` animations are reversible
        // and need to track previous properties just like any other transition.
        // This means that going out will *always* execute the out animation,
        // instead of reversing the in animation (if you switch to going out
        // in the middle of going in)
        return toAnimationTask(anims.map((anim)=>{
            if (typeof anim === 'function') {
                // Note that we don't have the locking behavior here; we can't
                // because we don't know what the preemptively unlock. Users must
                // manually do locking if they want
                return anim();
            }
            const task = anim.play({
                push: type === 'out'
            });
            // If it's an out animation, lock the node which will
            // prevent any other animations from running and
            // interrupting it
            if (type === 'out') {
                anim.lockNode();
            }
            return task;
        }).filter(Boolean));
    }
    prepare(type) {
        // Never fire thet same event twice
        if (this._lastEventType !== type) {
            this.fire(type);
            this._lastEventType = type;
        }
        // Unlock all the nodes that out animations locks. If we are animating in,
        // we might be reversing an out animation and need to ensure all these nodes
        // are unlocked and can be animated again
        if (type === 'in') {
            (this.animations.get('out') || []).forEach((anim)=>{
                if (typeof anim !== 'function') {
                    anim.unlockNode();
                }
            });
        }
    }
    has(type) {
        const anims = this.animations.get(type);
        return anims ? anims.length > 0 : false;
    }
    listen(handlers) {
        if (handlers.in) {
            this.eventHandlers.in.push(handlers.in);
        }
        if (handlers.out) {
            this.eventHandlers.out.push(handlers.out);
        }
        return ()=>{
            if (handlers.in) {
                this.eventHandlers.in = this.eventHandlers.in.filter((h)=>h !== handlers.in);
            }
            if (handlers.out) {
                this.eventHandlers.out = this.eventHandlers.out.filter((h)=>h !== handlers.out);
            }
        };
    }
    constructor(){
        _define_property(this, "animations", void 0);
        _define_property(this, "eventHandlers", void 0);
        _define_property(this, "_lastEventType", void 0);
        this.animations = new Map();
        this.eventHandlers = {
            in: [],
            out: []
        };
        this._lastEventType = null;
    }
}
export function makeTimeline() {
    return new AnimationTimeline();
}
const noop = noopTimeline();
export const TimelineContext = /*#__PURE__*/ React.createContext(noop);
export function useTimeline(timeline) {
    return useMemo(()=>timeline || makeTimeline(), [
        timeline
    ]);
}
export function useCurrentTimeline() {
    return useContext(TimelineContext);
}
// This hook provides a declarative way to listen to in/out animation events,
// similar to the imperative `timeline.listen`. The difference is this ensures
// the events are always fired regardless of the lifecycle status
export function useTimelineListeners(events) {
    const timeline = useCurrentTimeline();
    // We want to always make sure the `in` and `out` listeners are fired. The
    // technique for ensuring this differs between them becauase we are relying on
    // subtle semantics of how effects are timed.
    //
    // For `in` listeners: We need to check if the timeline has already fired the
    // `in` event. If that was the last event, we know it's already been run so we
    // need to manually invoke it.
    //
    // For `out` listeners: If we never receive an `out` event from the timeline,
    // we need to manually invoke it, and to do so we need to keep track of this
    // state. If our effect cleanup is invoked and the out handler hasn't been
    // called yet, that means we are unmounting forcibly and should call it
    // (`AnimatePresence` should have fired it by then)
    const inEventCalled = useRef(false);
    const latestOutEvent = useRef(events.out);
    const outEventCalled = useRef(false);
    useIsomorphicLayoutEffect(()=>{
        latestOutEvent.current = events.out;
        if ('_lastEventType' in timeline && timeline._lastEventType === 'in' && !inEventCalled.current) {
            var _events_in;
            (_events_in = events.in) === null || _events_in === void 0 ? void 0 : _events_in.call(events);
            inEventCalled.current = true;
        }
        return timeline.listen({
            in: ()=>{
                outEventCalled.current = false;
                if (inEventCalled.current === false) {
                    var _events_in;
                    inEventCalled.current = true;
                    (_events_in = events.in) === null || _events_in === void 0 ? void 0 : _events_in.call(events);
                }
            },
            out: ()=>{
                var _events_out;
                outEventCalled.current = true;
                inEventCalled.current = false;
                (_events_out = events.out) === null || _events_out === void 0 ? void 0 : _events_out.call(events);
            }
        });
    }, [
        events
    ]);
    useIsomorphicLayoutEffect(()=>{
        outEventCalled.current = false;
        return ()=>{
            if (!outEventCalled.current) {
                var _latestOutEvent_current;
                (_latestOutEvent_current = latestOutEvent.current) === null || _latestOutEvent_current === void 0 ? void 0 : _latestOutEvent_current.call(latestOutEvent);
            }
        };
    }, []);
}
export function TimelineProvider({ timeline, children }) {
    return /*#__PURE__*/ React.createElement(TimelineContext.Provider, {
        value: timeline
    }, children);
}

//# sourceMappingURL=timeline.js.map